/**
 * OverviewCards - Recreation of Base44 OverviewCards Component
 * Enhanced metrics display with resource type analytics and health indicators
 * 
 * @description Professional dashboard cards showing resource statistics by type
 * with health monitoring, status indicators, and provider-specific filtering
 */

import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Server, 
  Database, 
  HardDrive, 
  Network, 
  AlertTriangle,
  CheckCircle,
  XCircle 
} from 'lucide-react';

// Resource type configuration matching Base44 patterns
const resourceTypes = [
  { 
    type: 'compute', 
    title: 'Compute Instances', 
    icon: Server, 
    color: 'bg-blue-500',
    description: 'Virtual machines and containers'
  },
  { 
    type: 'storage', 
    title: 'Storage Buckets', 
    icon: HardDrive, 
    color: 'bg-green-500',
    description: 'Object storage and file systems'
  },
  { 
    type: 'database', 
    title: 'Databases', 
    icon: Database, 
    color: 'bg-purple-500',
    description: 'SQL and NoSQL databases'
  },
  { 
    type: 'network', 
    title: 'Network Resources', 
    icon: Network, 
    color: 'bg-orange-500',
    description: 'Load balancers and VPCs'
  }
];

// TypeScript interfaces for type safety
interface CloudResource {
  id: string;
  name: string;
  type: 'compute' | 'storage' | 'database' | 'network';
  provider: 'aws' | 'gcp' | 'azure';
  status: 'running' | 'stopped' | 'error' | 'pending';
  health: 'healthy' | 'warning' | 'critical';
}

interface OverviewCardsProps {
  resources: CloudResource[];
  activeProvider: string;
}

interface HealthStats {
  healthy: number;
  warning: number;
  critical: number;
  total: number;
  healthPercentage: number;
}

export function OverviewCards({ resources, activeProvider }: OverviewCardsProps) {
  // Filter resources by type and active provider
  const getResourcesByType = (type: string): CloudResource[] => {
    return resources.filter(r => r.type === type && r.provider === activeProvider);
  };

  // Calculate health statistics for resource type
  const getHealthStats = (typeResources: CloudResource[]): HealthStats => {
    const healthy = typeResources.filter(r => r.health === 'healthy').length;
    const warning = typeResources.filter(r => r.health === 'warning').length;
    const critical = typeResources.filter(r => r.health === 'critical').length;
    const total = typeResources.length;
    
    return { 
      healthy, 
      warning, 
      critical, 
      total, 
      healthPercentage: total ? (healthy / total) * 100 : 0 
    };
  };

  // Get appropriate status icon based on health percentage
  const getStatusIcon = (healthPercentage: number) => {
    if (healthPercentage >= 80) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (healthPercentage >= 50) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
      {resourceTypes.map(({ type, title, icon: Icon, color, description }) => {
        const typeResources = getResourcesByType(type);
        const { healthy, warning, critical, total, healthPercentage } = getHealthStats(typeResources);
        
        return (
          <Card key={type} className="relative overflow-hidden hover:shadow-lg transition-all duration-300 group">
            {/* Background decoration matching Base44 design */}
            <div className={`absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8 ${color} rounded-full opacity-5 group-hover:opacity-10 transition-opacity`} />
            
            <CardHeader className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium text-muted-foreground mb-2">{title}</CardTitle>
                  
                  {/* Main metrics display */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-3xl font-bold">{total}</span>
                    {getStatusIcon(healthPercentage)}
                  </div>
                  
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                
                {/* Icon container with colored background */}
                <div className={`p-3 rounded-xl ${color} bg-opacity-10 flex-shrink-0`}>
                  <Icon className={`w-6 h-6 !text-[var(--background)] ${color.replace('bg-', 'text-')}`} />
                </div>
              </div>
              
              {/* Health statistics section */}
              {total > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">Health Status</span>
                    <span className="font-medium">{Math.round(healthPercentage)}%</span>
                  </div>
                  
                  {/* Health progress bar */}
                  <Progress value={healthPercentage} className="h-2" />
                  
                  {/* Health breakdown */}
                  <div className="flex gap-4 text-xs mt-3">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-muted-foreground">{healthy} Healthy</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                      <span className="text-muted-foreground">{warning} Warning</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full" />
                      <span className="text-muted-foreground">{critical} Critical</span>
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
}

export default OverviewCards;